import { supabase } from '../supabase';

// Cache para filtros pré-carregados
let cachedCategories: string[] = [];
let cachedMechanics: string[] = [];
let cachedThemes: string[] = [];
let filtersLastUpdated: Date | null = null;

// Função para carregar os filtros disponíveis do banco de dados
export async function loadAvailableFilters(forceRefresh = false) {
  try {
    // Verificar se já temos os filtros em cache e se ainda são válidos (menos de 24 horas)
    const now = new Date();
    const cacheValid = filtersLastUpdated && 
                     ((now.getTime() - filtersLastUpdated.getTime()) < 24 * 60 * 60 * 1000);
    
    // Se os filtros já estão em cache e são válidos, retornar diretamente
    if (!forceRefresh && cacheValid && 
        cachedCategories.length > 0 && 
        cachedMechanics.length > 0 && 
        cachedThemes.length > 0) {
      console.log('Usando filtros em cache', { 
        categoriesCount: cachedCategories.length,
        mechanicsCount: cachedMechanics.length,
        themesCount: cachedThemes.length,
        lastUpdated: filtersLastUpdated
      });
      
      return {
        categories: cachedCategories,
        mechanics: cachedMechanics,
        themes: cachedThemes,
        lastUpdated: filtersLastUpdated
      };
    }
    
    console.log('Carregando filtros do banco de dados via RPC...');
    
    // Buscar todos os valores distintos para categorias, mecânicas e temas usando RPC
    const [categoriesResult, mechanicsResult, themesResult] = await Promise.all([
      supabase.rpc('get_distinct_game_attributes', { attribute_column: 'categories' }),
      supabase.rpc('get_distinct_game_attributes', { attribute_column: 'mechanics' }),
      supabase.rpc('get_distinct_game_attributes', { attribute_column: 'themes' })
    ]);
    
    // Tratar erros de RPC
    if (categoriesResult.error) throw categoriesResult.error;
    if (mechanicsResult.error) throw mechanicsResult.error;
    if (themesResult.error) throw themesResult.error;
    
    // Atualizar o cache com os dados recebidos (já vêm ordenados e em minúsculas da RPC)
    // A função RPC retorna um array vazio se não houver resultados, então não precisamos tratar null
    cachedCategories = categoriesResult.data || [];
    cachedMechanics = mechanicsResult.data || [];
    cachedThemes = themesResult.data || [];
    filtersLastUpdated = now;
    
    console.log('Filtros carregados com sucesso via RPC', {
      categoriesCount: cachedCategories.length,
      mechanicsCount: cachedMechanics.length,
      themesCount: cachedThemes.length
    });
    
    return {
      categories: cachedCategories,
      mechanics: cachedMechanics,
      themes: cachedThemes,
      lastUpdated: filtersLastUpdated
    };
  } catch (error) {
    console.error('Erro ao carregar filtros disponíveis via RPC:', error);
    // Em caso de erro, retornar o cache antigo se disponível, ou vazio
    // para evitar quebrar a aplicação se o carregamento inicial falhar.
    // Retornar um erro poderia ser uma alternativa, dependendo do requisito.
    return {
      categories: cachedCategories,
      mechanics: cachedMechanics,
      themes: cachedThemes,
      lastUpdated: filtersLastUpdated
    };
  }
}

// Função para obter os filtros disponíveis atualmente
export function getAvailableFilters() {
  return {
    categories: cachedCategories,
    mechanics: cachedMechanics,
    themes: cachedThemes,
    lastUpdated: filtersLastUpdated
  };
}

// Função para verificar se um valor está disponível nos filtros atuais
// Útil para validar entradas de usuário antes de enviar para o banco
export function isValidFilterValue(type: 'categories' | 'mechanics' | 'themes', value: string): boolean {
  value = value.toLowerCase();
  
  switch (type) {
    case 'categories':
      return cachedCategories.includes(value);
    case 'mechanics':
      return cachedMechanics.includes(value);
    case 'themes':
      return cachedThemes.includes(value);
    default:
      return false;
  }
}

// Carregar os filtros quando o módulo for importado
loadAvailableFilters().catch(err => console.error('Erro ao pré-carregar filtros:', err)); 